import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ClipboardDocumentCheckIcon, UserGroupIcon, UserIcon } from '@heroicons/react/24/outline';
import { contentApi, CONTENT_TYPE_LABEL, getContentRoute, MyAssignment } from '@/services/contentApi';

const statusBadgeClass = (status?: string) => {
  const s = (status || '').toLowerCase();
  if (['completed', 'published', 'approved'].includes(s)) {
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  }
  if (['failed', 'rejected', 'error'].includes(s)) {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  }
  if (s) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
};

const AssignmentRow: React.FC<{ item: MyAssignment }> = ({ item }) => {
  const navigate = useNavigate();
  const route = getContentRoute(item.contentType, item.contentId);

  return (
    <div
      onClick={() => navigate(route)}
      className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {item.kind === 'review' && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
              <ClipboardDocumentCheckIcon className="h-3 w-3" /> Review
            </span>
          )}
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {CONTENT_TYPE_LABEL[item.contentType]}
          </span>
          {item.status && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadgeClass(item.status)}`}>
              {item.status}
            </span>
          )}
        </div>
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate mt-0.5">{item.title}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {item.kind === 'review' ? 'Review requested' : 'Assigned'} {formatDistanceToNow(new Date(item.assignedAt), { addSuffix: true })}
          {item.assignedBy?.name ? ` by ${item.assignedBy.name}` : ''}
        </p>
      </div>
      <div className="flex-shrink-0 ml-4">
        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-full px-2.5 py-1">
          {item.assigneeType === 'team' ? <UserGroupIcon className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}
          {item.assigneeName}
        </span>
      </div>
    </div>
  );
};

const MyAssignmentsPage: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['my-assignments'],
    queryFn: () => contentApi.getMyAssignments(),
  });

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <ClipboardDocumentCheckIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">My Assignments</h1>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      ) : !data?.length ? (
        <div className="text-center py-16 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
          <ClipboardDocumentCheckIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nothing assigned to you or your teams right now.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.map((item) => (
            <AssignmentRow key={`${item.contentType}:${item.contentId}`} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MyAssignmentsPage;
